import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { format, parse, isValid } from 'date-fns';

import { Popover, PopoverTrigger, PopoverContent } from './popover';
import { cn } from '../../lib/utils';

/**
 * Local-timezone helpers — keep "YYYY-MM-DD" stable across PST/UTC boundaries.
 * Avoids the classic `new Date('2026-05-25')` shifting to the 24th in west-of-UTC.
 */
const parseIso = (v) => {
  if (!v) return undefined;
  if (v instanceof Date) return v;
  const d = parse(v, 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : undefined;
};
const toIso = (d) => (d ? format(d, 'yyyy-MM-dd') : '');

/**
 * Try to read whatever the user typed into the MM/DD/YYYY field. Accepts:
 *   05/25/2026, 5/25/26, 05-25-2026, 2026-05-25
 * Returns a Date or null. Permissive — fails open, only locks-in when valid.
 */
const tryParseTyped = (raw) => {
  const v = (raw || '').trim();
  if (!v) return null;
  const fmts = ['MM/dd/yyyy', 'M/d/yyyy', 'MM/dd/yy', 'M/d/yy', 'MM-dd-yyyy', 'yyyy-MM-dd'];
  for (const f of fmts) {
    const d = parse(v, f, new Date());
    if (isValid(d)) return d;
  }
  return null;
};

/**
 * DatePopover — type-in OR pick-on-calendar replacement for native <input type="date">.
 *
 * Props:
 *   value, onChange (yyyy-MM-dd string), min, placeholder, className, testId,
 *   disabled, align — same surface as before.
 */
export function DatePopover({
  value,
  onChange,
  min,
  placeholder = 'mm/dd/yyyy',
  className,
  testId,
  disabled = false,
  align = 'start',
}) {
  const selected = parseIso(value);
  const minDate = parseIso(min);
  const [open, setOpen] = useState(false);

  // Local "typed" buffer for the text input. Kept in MM/DD/YYYY so the broker
  // can paste/type naturally. Reconciles to the canonical iso `value` whenever
  // that prop changes externally (calendar pick, reset, etc).
  const [typed, setTyped] = useState(selected ? format(selected, 'MM/dd/yyyy') : '');
  useEffect(() => {
    setTyped(selected ? format(selected, 'MM/dd/yyyy') : '');
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitTyped = () => {
    const d = tryParseTyped(typed);
    if (d) {
      // Respect `min`: ignore typed dates before the floor instead of silently
      // accepting them. The broker can correct without scary error toasts.
      if (minDate && d < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())) {
        // snap back to current value
        setTyped(selected ? format(selected, 'MM/dd/yyyy') : '');
        return;
      }
      onChange(toIso(d));
      setTyped(format(d, 'MM/dd/yyyy'));
    } else if (!typed.trim()) {
      onChange('');
    } else {
      // Unparseable — revert to last valid value so the input doesn't lie.
      setTyped(selected ? format(selected, 'MM/dd/yyyy') : '');
    }
  };

  return (
    <div className={cn('relative w-full', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        {/* Hairline-bordered input wrapper styled to match other modal inputs */}
        <div
          className={cn(
            'w-full h-10 px-3 rounded-lg border hairline bg-white flex items-center gap-2 transition',
            'focus-within:border-neutral-900 hover:border-neutral-400',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          <CalendarIcon className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
          <input
            type="text"
            inputMode="numeric"
            data-testid={testId}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onBlur={commitTyped}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitTyped(); }
              if (e.key === 'Escape') setTyped(selected ? format(selected, 'MM/dd/yyyy') : '');
            }}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            className="flex-1 bg-transparent text-[13px] font-semibold text-neutral-900 placeholder:text-neutral-400 placeholder:font-normal focus:outline-none tabular-nums"
          />
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={testId ? `${testId}-trigger` : undefined}
              disabled={disabled}
              aria-label="Open calendar"
              className="h-7 w-7 inline-flex items-center justify-center rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition shrink-0"
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </div>

        <PopoverContent
          data-testid={testId ? `${testId}-popover` : undefined}
          align={align}
          sideOffset={6}
          // z-[80] keeps the calendar above z-[60] confirm modals and z-50
          // shadcn defaults. Portal render ensures it escapes overflow.
          className="w-auto p-0 bg-white border hairline rounded-2xl shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] overflow-hidden z-[80]"
        >
          <DayPicker
            mode="single"
            selected={selected}
            onSelect={(d) => {
              onChange(toIso(d));
              if (d) setOpen(false);
            }}
            defaultMonth={selected || minDate || new Date()}
            disabled={minDate ? { before: minDate } : undefined}
            showOutsideDays
            weekStartsOn={0}
            formatters={{ formatWeekdayName: (d) => format(d, 'EEEEEE') /* Su, Mo, Tu */ }}
            components={{
              IconLeft: () => <ChevronLeft className="h-4 w-4" strokeWidth={2.25} />,
              IconRight: () => <ChevronRight className="h-4 w-4" strokeWidth={2.25} />,
            }}
            classNames={{
              root: 'p-4',
              months: 'flex flex-col',
              month: 'space-y-3',
              caption: 'relative h-9 flex items-center justify-center',
              caption_label: 'display text-[15px] font-bold tracking-tight text-neutral-900',
              nav: 'flex items-center',
              nav_button:
                'absolute h-8 w-8 inline-flex items-center justify-center rounded-lg border hairline bg-white text-neutral-700 hover:border-neutral-400 hover:text-neutral-900 transition',
              nav_button_previous: 'left-0',
              nav_button_next: 'right-0',
              table: 'w-full border-collapse',
              head_row: 'flex',
              head_cell:
                'w-9 h-8 text-[11px] uppercase tracking-wider font-semibold text-neutral-400 flex items-center justify-center',
              row: 'flex w-full mt-1',
              cell: 'w-9 h-9 p-0 text-center relative',
              day: 'w-9 h-9 inline-flex items-center justify-center rounded-lg text-[13px] font-semibold text-neutral-800 hover:bg-neutral-100 transition cursor-pointer',
              day_selected:
                'bg-neutral-900 text-white hover:bg-neutral-900 hover:text-white shadow-[0_4px_12px_-4px_rgba(0,0,0,0.4)]',
              day_today: 'ring-1 ring-[hsl(var(--kf-gold))] ring-inset',
              day_outside: 'text-neutral-300 hover:bg-transparent',
              day_disabled: 'text-neutral-300 opacity-50 cursor-not-allowed hover:bg-transparent',
              day_hidden: 'invisible',
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default DatePopover;
